import { useBaby } from '@/contexts/BabyContext';
import {
  ageInMonths,
  getCurrentSolidsWeek,
  getFoodsForWeek,
} from '@/lib/solidFoodSchedule';
import type { DietPreference } from '@/lib/solidFoodSchedule';
import { getMonthGuideForSolidsAge, WEEK1_PLAN } from '@/lib/solidsGuideContent';

interface MealPlanCardProps {
  /** Meal count already logged today (for showing ✓ on slots) */
  mealLoggedCount: number;
  /** Called when user taps Log on a slot; passes suggested food name */
  onLogMeal: (suggestion: string) => void;
}

/** Return today's day index within week 1 plan (0–6) */
function getWeek1DayIndex(solidsStartDate: string): number {
  const start = new Date(solidsStartDate);
  start.setHours(0, 0, 0, 0);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const days = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  return Math.min(days, 6); // clamp to 6 (WEEK1_PLAN has 7 entries, index 0–6)
}

const MealPlanCard = ({ mealLoggedCount, onLogMeal }: MealPlanCardProps) => {
  const { activeBaby } = useBaby();

  if (!activeBaby?.solidsStartDate) return null;

  const babyAgeMonths = ageInMonths(activeBaby.dob);
  const dietPref = activeBaby.dietPreference as DietPreference | undefined;
  const currentWeek = getCurrentSolidsWeek(activeBaby.solidsStartDate);
  const monthGuide = getMonthGuideForSolidsAge(activeBaby.solidsStartDate);
  const slots = monthGuide.mealSlots; // e.g. ['Lunch', 'Dinner'] or ['Breakfast', 'Lunch', 'Dinner']

  // Get food suggestions for each slot
  let suggestions: string[] = [];

  if (currentWeek === 1) {
    // Week 1: use day-specific plan
    const dayIdx = getWeek1DayIndex(activeBaby.solidsStartDate);
    const dayPlan = WEEK1_PLAN[dayIdx];
    suggestions = slots.map(() => dayPlan.food);
  } else {
    // Week 2+: distribute week foods across slots
    const weekFoods = getFoodsForWeek(currentWeek, dietPref, babyAgeMonths);
    suggestions = slots.map((_, i) =>
      weekFoods.length > 0
        ? `${weekFoods[i % weekFoods.length].emoji} ${weekFoods[i % weekFoods.length].name}`
        : 'Any familiar food'
    );
  }

  return (
    <div
      className="w-full rounded-2xl overflow-hidden"
      style={{
        background: 'hsl(var(--feed-bg))',
        border: '1px solid hsl(var(--feed-accent) / 0.2)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3.5 pb-2">
        <div>
          <p style={{ fontSize: 13, fontWeight: 700, color: 'hsl(var(--feed-accent))' }}>
            Today's meal plan
          </p>
          <p className="text-xs text-muted-foreground">
            Week {currentWeek} · {slots.length} meal{slots.length !== 1 ? 's' : ''} scheduled
          </p>
        </div>
        <span className="text-xl">🥄</span>
      </div>

      {/* Meal slots */}
      <div className="px-4 pb-3.5 space-y-2">
        {slots.map((slot, idx) => {
          const isLogged = idx < mealLoggedCount;
          return (
            <div
              key={slot}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5"
              style={{ background: 'hsl(var(--background) / 0.6)' }}
            >
              {/* Status indicator */}
              <div
                className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                style={{
                  background: isLogged
                    ? 'hsl(var(--diaper-accent))'
                    : 'hsl(var(--feed-accent) / 0.15)',
                }}
              >
                {isLogged ? (
                  <span style={{ fontSize: 11, color: '#fff' }}>✓</span>
                ) : (
                  <span style={{ fontSize: 11, color: 'hsl(var(--feed-accent))' }}>
                    {idx + 1}
                  </span>
                )}
              </div>

              {/* Slot info */}
              <div className="flex-1 min-w-0">
                <p style={{ fontSize: 12, fontWeight: 700, color: 'hsl(var(--foreground))' }}>
                  {slot}
                </p>
                <p className="text-xs text-muted-foreground truncate">{suggestions[idx]}</p>
              </div>

              {/* Log button */}
              {!isLogged && (
                <button
                  onClick={() => onLogMeal(suggestions[idx])}
                  className="flex-shrink-0 rounded-full px-3 py-1 text-xs font-semibold active:scale-95 transition-transform"
                  style={{
                    background: 'hsl(var(--feed-accent))',
                    color: '#fff',
                  }}
                >
                  Log
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MealPlanCard;
